import { type ButtonHTMLAttributes, forwardRef } from "react";
import clsx from "clsx";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize    = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?:    ButtonSize;
  loading?: boolean;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    "bg-accent text-white hover:bg-[var(--color-accent-hover)]" +
    " focus-visible:ring-2 focus-visible:ring-accent",
  secondary:
    "bg-surface border border-border text-fg hover:border-muted" +
    " focus-visible:ring-2 focus-visible:ring-accent",
  ghost:
    "bg-transparent text-fg hover:bg-surface" +
    " focus-visible:ring-2 focus-visible:ring-accent",
  danger:
    "bg-danger text-white hover:bg-[var(--color-danger-hover)]" +
    " focus-visible:ring-2 focus-visible:ring-danger",
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: "h-7  px-2.5 text-xs",
  md: "h-8  px-3.5 text-sm",
  lg: "h-10 px-5   text-sm",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "primary", size = "md", loading = false, disabled, className, children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={clsx(
        "inline-flex items-center justify-center gap-1.5 rounded-md font-medium transition-colors",
        "focus-visible:outline-none ring-offset-bg ring-offset-1",
        "disabled:opacity-40 disabled:cursor-not-allowed select-none",
        variantClasses[variant],
        sizeClasses[size],
        className,
      )}
      {...rest}
    >
      {loading && (
        <span
          aria-hidden
          className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
        />
      )}
      {children}
    </button>
  );
});
